import { useMemo } from 'react';

import type { OrderData } from '@shared/dream-components';
import { useQuery } from '@tanstack/react-query';

import { ProductType } from '~/interfaces/product';

export const subscriberOrdersKeys = {
  all: ['subscriber-orders'] as const,
  bySubscriber: (subscriberId: string) => ['subscriber-orders', subscriberId] as const,
};

const fetchSubscriberOrders = async (): Promise<OrderData[]> => {
  const response = await fetch('/subscriber_orders');
  if (!response.ok) throw new Error('Failed to fetch subscriber orders');
  return response.json();
};

interface Props {
  enabled: boolean;
  subscriberId: string;
}

interface ReturnProps {
  isLoading: boolean;
  data: OrderData[];
  enabled?: boolean;
  refetch: () => void;
}

const useStaticOrders = ({ enabled, subscriberId }: Props): ReturnProps => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: subscriberOrdersKeys.bySubscriber(subscriberId),
    queryFn: fetchSubscriberOrders,
    enabled: enabled && !!subscriberId,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  const transformedData = useMemo(() => {
    return data?.map((order) => {
      return {
        ...order,
        order_items: order.order_items.map((item) => {
          const canRescheduleBooking =
            item.product_variant.type === ProductType.COACHING_SESSION
              ? !item.fulfillment?.metadata?.cal_booking_reschedule_disabled
              : false;

          return {
            ...item,
            hide_access_fulfillment_button:
              item.product_variant.type === ProductType.COACHING_SESSION && !canRescheduleBooking,
          };
        }),
      };
    });
  }, [data]);

  return {
    refetch,
    isLoading: isLoading && enabled,
    data: transformedData || [],
  };
};

export default useStaticOrders;
